<template>
	<Connections :currentProjectId="currentProjectId" :canCreateNew="canCreateNew" :connectionsBySections="connectionsBySections" />
</template>

<script>
import Connections from './Connections';
import { mapActions, mapGetters } from 'vuex';
import ProjectService from '@/utils/projects-service';
import { mapGettersShorthand } from '@/store/utils/mappers';
import CredentialsService from '@/utils/credentials-service';
import { projectsList } from '@/store/modules/projects/getters';
import { projectHasPermission } from '@/store/modules/projects/getters';
import { projectCredentials } from '@/store/modules/credentials/getters';
import { selectedProjectId } from '@/store/modules/selectedProject/getters';
import { getAllAccountConnections } from '@/store/modules/credentials/getters';
import { VIRTUAL_PROJECT_IDS } from '@/utils/projects.const';
import { ProjectPermissions } from '@ply-io/ply-front-shared-utilities';
import { getProjectCredentials, getUserCredentials } from '@/store/modules/credentials/actions';

export default {
	name: 'SpaceConnections',
	components: { Connections },
	computed: {
		...mapGetters([
			selectedProjectId.fullPath,
			projectCredentials.fullPath,
			projectHasPermission.fullPath,
			getAllAccountConnections.fullPath,
		]),
		...mapGettersShorthand({ projectsList }),
		connectionsBySections() {
			return [
				{
					title: 'Space connections',
					subTitle: 'Collaborators have access',
					sourceList: this.projectConnections,
				},
				{
					title: 'Sensitive space connections',
					subTitle: 'Space admins have access',
					sourceList: this.sensitiveProjectConnections,
				},
				{
					sourceList: this.userConnections,
					title: this.personalConnectionsTitle,
					subTitle: this.personalConnectionsDescription,
					alwaysShowProjectChip: !this.isPersonalProject,
				},
			];
		},
		personalConnectionsDescription() {
			if (!this.project) {
				return '';
			} else {
				return 'Only you have access to see all personal connection';
			}
		},
		personalConnectionsTitle() {
			if (!this.project) {
				return '';
			} else {
				return 'Personal connections used in space';
			}
		},
		project() {
			return this.projectsList.find((proj) => proj.id == this.currentProjectId);
		},
		isPersonalProject() {
			return ProjectService.isPersonalProject(this.project);
		},
		projectToCreateConnectionIn() {
			return VIRTUAL_PROJECT_IDS.includes(this.currentProjectId) ? undefined : Number(this.currentProjectId);
		},
		canCreateNew() {
			if (this.projectToCreateConnectionIn) {
				return this[projectHasPermission.fullPath](this.currentProjectId, ProjectPermissions.ChangeCredentials);
			}
			return true;
		},
		currentProjectId() {
			return this[selectedProjectId.fullPath];
		},
		allAccountConnections() {
			return Object.values(this[getAllAccountConnections.fullPath]);
		},
		userConnections() {
			if (this.currentProjectIsVirtual) {
				return this.allAccountConnections;
			}
			if (this.isPersonalProject) {
				return this.allAccountConnections.filter((connection) => !connection.access?.is_related);
			}
			return this.currentProjectConnections.filter((c) => !c.access?.related_entity_type);
		},
		sensitiveProjectConnections() {
			return this.currentProjectConnections.filter((c) =>
				CredentialsService.isConnectionRelatedToProjectAndSensitive(c, this.currentProjectId)
			);
		},
		projectConnections() {
			return this.currentProjectConnections.filter((c) =>
				CredentialsService.isConnectionRelatedToProjectAndNotSensitive(c, this.currentProjectId)
			);
		},
		currentProjectConnections() {
			return this[projectCredentials.fullPath](this.currentProjectId) || [];
		},

		currentProjectIsVirtual() {
			return VIRTUAL_PROJECT_IDS.includes(this.currentProjectId);
		},
	},
	methods: {
		...mapActions([getUserCredentials.fullPath, getProjectCredentials.fullPath]),
	},
	async mounted() {
		this[getUserCredentials.fullPath]({ used_in: true });
		if (!this.currentProjectIsVirtual) {
			// force getting new project credentials so that we see all credentials that were used in the workflows and projects if changed
			this[getProjectCredentials.fullPath]({ projectId: this.currentProjectId, forceGet: true });
		}
	},
};
</script>

<style scoped lang="scss"></style>
